import React from 'react';
import { getChallengeTypeById } from '../../config/challengeTypes';

interface SectionContentItem {
    projectSectionContentId?: string;
    sectionName?: string;
    contentType?: string;
    content: any;
    isEditable?: boolean;
    isIncluded?: boolean;
}

interface ContentViewerReadOnlyProps {
    item: SectionContentItem | null;
    onBack: () => void;
}

const ContentViewerReadOnly: React.FC<ContentViewerReadOnlyProps> = ({ item, onBack }) => {
    if (!item) {
        return (
            <div className="content-viewer-readonly p-4">
                <div className="text-center text-muted">
                    <p>No hay contenido para mostrar</p>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={onBack}
                    >
                        ← Volver
                    </button>
                </div>
            </div>
        );
    }

    const getContentTypeLabel = (contentType?: string) => {
        switch (contentType) {
            case 'TEXT_SIMPLE':
                return 'Texto Simple';
            case 'TEXT_SELECTABLE':
                return 'Texto Seleccionable';
            case 'QUESTION_SIMPLE':
                return 'Pregunta Simple';
            case 'QUESTION_MULTIPLE':
                return 'Pregunta Multiple';
            case 'QUESTION_IMAGE':
                return 'Pregunta con Imagen';
            case 'CHALLENGE':
                return 'Desafío';
            default:
                return contentType || 'Tipo desconocido';
        }
    };

    const renderContentEditor = () => {
        const contentType = item.contentType;

        switch (contentType) {
            case 'TEXT_SIMPLE':
                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light" style={{ minHeight: '100px' }}>
                            <div dangerouslySetInnerHTML={{ __html: item.content || 'Sin contenido' }} />
                        </div>
                    </div>
                );

            case 'TEXT_SELECTABLE':
                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light" style={{ minHeight: '100px' }}>
                            <div dangerouslySetInnerHTML={{ __html: item.content || 'Sin contenido' }} />
                        </div>
                    </div>
                );

            case 'QUESTION_SIMPLE':
                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light" style={{ minHeight: '100px' }}>
                            <div dangerouslySetInnerHTML={{ __html: item.content || 'Sin contenido' }} />
                        </div>
                    </div>
                );

            case 'QUESTION_MULTIPLE':
                const questionContent = typeof item.content === 'object' ? item.content : {};
                const questionText = questionContent.questionText || (typeof item.content === 'string' ? item.content : '');
                const answers = questionContent.answers || [];
                const allowMultiple = questionContent.allowMultiple || false;

                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light">
                            <div className="mb-3">
                                <strong>Pregunta:</strong>
                                <div dangerouslySetInnerHTML={{ __html: questionText || 'Sin pregunta' }} />
                            </div>
                            {answers.length > 0 && (
                                <div>
                                    <strong>Opciones ({allowMultiple ? 'múltiple' : 'única'}):</strong>
                                    <ul className="mt-2">
                                        {answers.map((answer: any, index: number) => (
                                            <li key={index} className="mb-1">
                                                <span dangerouslySetInnerHTML={{ __html: answer.text || `Opción ${index + 1}` }} />
                                                {answer.isFreeText && <em> (permite texto libre)</em>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'QUESTION_IMAGE':
                const imageContent = typeof item.content === 'object' ? item.content : {};
                const imageText = imageContent.text || (typeof item.content === 'string' ? item.content : '');
                const imageUrl = imageContent.imageUrl || '';
                const imageFilename = imageContent.imageFilename || '';

                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light">
                            <div className="mb-3">
                                <strong>Texto:</strong>
                                <div dangerouslySetInnerHTML={{ __html: imageText || 'Sin texto' }} />
                            </div>
                            {(imageUrl || imageFilename) && (
                                <div>
                                    <strong>Imagen:</strong>
                                    <div className="mt-2">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={imageFilename || 'Imagen'} style={{ maxWidth: '100%', height: 'auto' }} />
                                        ) : (
                                            <p className="text-muted">Archivo: {imageFilename}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'CHALLENGE':
                const challengeType = item.content?.challengeType;
                const challengeConfig = getChallengeTypeById(challengeType);

                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light">
                            <div className="mb-3">
                                <strong>Tipo de Challenge:</strong>
                                <div className="mt-1">
                                    {challengeConfig ? (
                                        <div>
                                            <span className="badge bg-primary me-2">{challengeConfig.name}</span>
                                        </div>
                                    ) : (
                                        <span className="text-warning">
                                            Tipo desconocido: {challengeType || 'No especificado'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {item.content?.config && Object.keys(item.content.config).length > 0 && (
                                <div>
                                    <strong>Configuración:</strong>
                                    <div className="mt-2 p-2 bg-white rounded border">
                                        <pre style={{ fontSize: '0.85rem', margin: 0 }}>
                                            {JSON.stringify(item.content.config, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}

                            {challengeConfig && (
                                <div className="mt-3">
                                    <small className="text-info">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Componente: {challengeConfig.componentName}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                );

            default:
                // Fallback for unknown content types
                return (
                    <div className="content-display">
                        <div className="border rounded p-3 bg-light">
                            <pre>{JSON.stringify(item.content, null, 2)}</pre>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="content-viewer-readonly">
            {/* Header with back button */}
            <div className="d-flex align-items-center mb-4 border-bottom pb-3">
                <button
                    type="button"
                    className="btn btn-link p-0 me-3"
                    onClick={onBack}
                    title="Volver al listado"
                >
                    <i className="bi bi-arrow-left fs-4"></i>
                </button>
                <div>
                    <h5 className="mb-1">Vista de Contenido</h5>
                    <small className="text-muted">Solo lectura</small>
                </div>
            </div>

            {/* Content details */}
            <div className="content-details">
                {/* Title */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Nombre:</label>
                    <p className="mb-0">{item.sectionName || 'Sin título'}</p>
                </div>

                {/* Content Type */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Tipo:</label>
                    <p className="mb-0">
                        <span className="badge bg-info text-dark">
                            {getContentTypeLabel(item.contentType)}
                        </span>
                    </p>
                </div>

                {/* Inclusion Status */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Estado:</label>
                    <p className="mb-0">
                        <span
                            className={`badge ${item.isIncluded !== false ? 'bg-success' : 'bg-warning text-dark'}`}
                        >
                            {item.isIncluded !== false ? 'Incluido' : 'No incluido'}
                        </span>
                    </p>
                </div>

                {/* Content */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Contenido:</label>
                    <div>
                        {renderContentEditor()}
                    </div>
                </div>

                {/* Footer with back button */}
                <div className="text-center mt-4">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={onBack}
                    >
                        ← Volver al listado
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContentViewerReadOnly;